/**
 * Text component - универсальный текстовый компонент для секций
 * Работает в React и в статике
 *
 * Может принимать:
 * - data={{ text: "...", size: "xl", weight: "bold", color: "white" }}
 * - или просто children + отдельные props
 */

// Пресеты для быстрого использования
const presets = {
  // Заголовки
  h1: {
    as: 'h1',
    size: '4xl',
    weight: 'bold',
    color: 'inherit',
  },
  h2: {
    as: 'h2',
    size: '3xl',
    weight: 'bold',
    color: 'inherit',
  },
  h3: {
    as: 'h3',
    size: '2xl',
    weight: 'semibold',
    color: 'inherit',
  },
  h4: {
    as: 'h4',
    size: 'xl',
    weight: 'semibold',
    color: 'inherit',
  },
  // Тексты
  subtitle: {
    as: 'p',
    size: 'xl',
    weight: 'normal',
    color: 'text-secondary',
  },
  body: {
    as: 'p',
    size: 'base',
    weight: 'normal',
    color: 'text-secondary',
  },
  'body-lg': {
    as: 'p',
    size: 'lg',
    weight: 'normal',
    color: 'text-secondary',
  },
  'body-sm': {
    as: 'p',
    size: 'sm',
    weight: 'normal',
    color: 'text-secondary',
  },
  caption: {
    as: 'span',
    size: 'xs',
    weight: 'normal',
    color: 'muted',
  },
  label: {
    as: 'span',
    size: 'sm',
    weight: 'medium',
    color: 'inherit',
  },
};

// Классы размеров
const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
};

// Классы веса
const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

// Классы цвета
const colorClasses = {
  inherit: '',
  white: 'text-white',
  'white-muted': 'text-white/90',
  foreground: 'text-foreground',
  'text-secondary': 'text-text-secondary',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
};

// Классы выравнивания
const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export default function Text({
  children,
  data,              // Объект { text, size, weight, color } - альтернатива отдельным props
  variant,           // Пресет: h1, h2, h3, h4, subtitle, body, body-lg, body-sm, caption, label
  as,                // HTML тег: h1, h2, h3, h4, h5, h6, p, span, div
  size,              // Размер: xs, sm, base, lg, xl, 2xl, 3xl, 4xl, 5xl
  weight,            // Вес: normal, medium, semibold, bold
  color,             // Цвет: inherit, white, white-muted, foreground, text-secondary, muted, primary
  align,             // Выравнивание: left, center, right
  defaults = {},     // Значения по умолчанию (из секции)
  className = '',    // Дополнительные классы
  style,             // Inline стили
  ...props
}) {
  // Получаем пресет если указан
  const preset = variant ? presets[variant] : {};

  // Если передан data объект, извлекаем из него значения
  const dataText = data?.text;
  const dataSize = data?.size;
  const dataWeight = data?.weight;
  const dataItalic = data?.italic;
  const dataColor = data?.color;
  const dataMultiline = data?.multiline;

  // Приоритет: props > data > defaults > preset
  const Tag = as || preset.as || 'p';
  const finalSize = size || dataSize || defaults.size || preset.size || 'base';
  const finalWeight = weight || dataWeight || defaults.weight || preset.weight || 'normal';
  const finalItalic = dataItalic ?? defaults.italic ?? false;
  const finalColor = color || dataColor || defaults.color || preset.color || 'inherit';
  const finalAlign = align;
  const finalMultiline = dataMultiline;
  const content = children || dataText;

  // Не рендерим если нет контента
  if (!content) return null;

  // Собираем классы
  const classes = [
    // Размер
    sizeClasses[finalSize] || `text-${finalSize}`,
    // Вес
    weightClasses[finalWeight] || '',
    // Курсив
    finalItalic ? 'italic' : '',
    // Цвет
    colorClasses[finalColor] || finalColor,
    // Выравнивание
    finalAlign ? alignClasses[finalAlign] || '' : '',
    // Многострочный текст - сохраняем переносы
    finalMultiline ? 'whitespace-pre-wrap' : '',
    // Дополнительные классы
    className,
  ].filter(Boolean).join(' ');

  return (
    <Tag className={classes} style={style} {...props}>
      {content}
    </Tag>
  );
}

// Экспорт пресетов для использования в настройках
Text.presets = Object.keys(presets);
Text.sizes = Object.keys(sizeClasses);
Text.weights = Object.keys(weightClasses);
Text.colors = Object.keys(colorClasses);
